//Cart Drawer Custom Element JS

if (!customElements.get("cart-drawer")) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.closeButton = this.querySelector(".drawer__close");
      if (this.closeButton) {
        this.closeButton.addEventListener("click", this.closeDrawer.bind(this));
      }

      // Add ARIA live region for cart updates
      this.ariaLiveRegion = document.createElement("div");
      this.ariaLiveRegion.setAttribute("aria-live", "polite");
      this.ariaLiveRegion.setAttribute("aria-atomic", "true");
      this.ariaLiveRegion.className = "sr-only";
      this.appendChild(this.ariaLiveRegion);

      // Store trigger button for focus restoration
      this.triggerButton = null;

      if (window.location.search.indexOf("viewcart=true") > -1) {
        this.openDrawer();
      }

      document.addEventListener("octane.quiz.addToCart", (event) => {
        this.openDrawer();
      });
    }

    // By Call this Function you can open Cart Drawer by this document.querySelector('cart-drawer').openDrawer();
    openDrawer = () => {
      // Store trigger button for focus restoration on close
      this.triggerButton = document.activeElement;

      this.classList.add("show-overlay");
      document.body.classList.add("overlay-open");
      lockBodyScroll();

      // Move focus to close button for keyboard users
      if (this.closeButton) {
        setTimeout(() => {
          this.closeButton.focus();
        }, 100);
      }

      // Announce to screen readers
      this.ariaLiveRegion.textContent =
        "Shopping cart opened. Press Escape or click close to dismiss.";

      // Dispatch cart-drawer:opened event for recommendations manager
      document.dispatchEvent(new CustomEvent("cart-drawer:opened"));
    };

    // By Call this Function you can close Cart Drawer by this document.querySelector('cart-drawer').closeDrawer();
    closeDrawer = () => {
      this.classList.remove("show-overlay");
      document
        .querySelector(".link-item-with-icon.cart-link")
        .classList.remove("active");
      document.body.classList.remove("overlay-open");
      unlockBodyScroll();

      // Restore focus to trigger button
      if (this.triggerButton) {
        this.triggerButton.focus();
      }

      // Clear aria-live message
      this.ariaLiveRegion.textContent = "";
    };
  }

  customElements.define("cart-drawer", CartDrawer);
}

if (!customElements.get("quantity-selector")) {
  class QuantitySelector extends HTMLElement {
    constructor() {
      super();
      this.buttonsPlus = this.querySelector('button[name="plus"]');
      this.buttonsMinus = this.querySelector('button[name="minus"]');
      this.buttonRemove = this.nextElementSibling;
      this.input = this.querySelector("input");
      this.currentValue = this.input.value;
      this.isUpdating = false;

      this.applyMaxToProduct =
        this.dataset.applyMaxToProduct === "true" ||
        this.dataset.applyMaxToProduct === true ||
        !this.dataset.applyMaxToProduct;
      this.variantId = this.dataset.variantId
        ? parseInt(this.dataset.variantId)
        : null;

      // New metafield-based max quantity
      this.productMaxQuantity = this.dataset.productMaxQuantity
        ? parseInt(this.dataset.productMaxQuantity)
        : this.dataset.maxQuantity ? parseInt(this.dataset.maxQuantity) : null;
      this.variantMaxQuantity = this.dataset.variantMaxQuantity
        ? parseInt(this.dataset.variantMaxQuantity)
        : null;

      this.buttonsPlus
        ? this.buttonsPlus.addEventListener("click", this.upQuantity.bind(this))
        : "";
      this.buttonsMinus
        ? this.buttonsMinus.addEventListener(
            "click",
            this.downQuantity.bind(this)
          )
        : "";
      this.buttonRemove
        ? this.buttonRemove.addEventListener(
            "click",
            this.removeQuantity.bind(this)
          )
        : "";
      this.input
        ? this.input.addEventListener("change", this.changeQuantity.bind(this))
        : "";

      this.checkMaxQuantity();
    }

    removeQuantity = () => {
      this.updateCart(true);
    };

    changeQuantity = (e) => {
      this.updateCart(false);
      if (!this.checkMaxQuantity(true)) {
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        return false;
      }
    };

    upQuantity = async () => {
      // Prevent spam clicks - if already updating, ignore
      if (this.isUpdating) {
        return;
      }

      const inputMax = this.input.max ? parseInt(this.input.max) : null;
      const currentValue = parseInt(this.input.value);
      const productId = parseInt(this.input.dataset.productId);

      try {
        const cartResponse = await fetch("/cart.js");
        const cartData = await cartResponse.json();

        let shouldBlock = false;

        // Check against input max attribute first
        if (inputMax && currentValue >= inputMax) {
          shouldBlock = true;
          this.buttonsPlus.disabled = true;
          this.input.disabled = true;
          return;
        }

        if (this.applyMaxToProduct) {
          // Apply BOTH product-level max AND variant-level max

          // Check product-level max
          const productMax =
            this.productMaxQuantity ||
            (this.dataset.maxQuantity
              ? parseInt(this.dataset.maxQuantity)
              : null);
          if (productMax) {
            let totalProductQuantity = 0;
            cartData.items.forEach((item) => {
              if (item.product_id == productId) {
                totalProductQuantity += item.quantity;
              }
            });

            if (totalProductQuantity >= productMax) {
              shouldBlock = true;
            }
          }

          // Check variant-level max for THIS variant
          if (this.variantMaxQuantity) {
            let variantQuantity = 0;
            cartData.items.forEach((item) => {
              if (item.variant_id == this.variantId) {
                variantQuantity += item.quantity;
              }
            });

            if (variantQuantity >= this.variantMaxQuantity) {
              shouldBlock = true;
            }
          }
        } else {
          // applyMaxToProduct is false: only check variant-level max
          if (this.variantMaxQuantity) {
            let variantQuantity = 0;
            cartData.items.forEach((item) => {
              if (item.variant_id == this.variantId) {
                variantQuantity += item.quantity;
              }
            });

            if (variantQuantity >= this.variantMaxQuantity) {
              shouldBlock = true;
            }
          } else {
            // Fall back to legacy maxQuantity
            const legacyMax = this.dataset.maxQuantity
              ? parseInt(this.dataset.maxQuantity)
              : null;
            if (legacyMax) {
              let variantQuantity = 0;
              cartData.items.forEach((item) => {
                if (item.variant_id == this.variantId) {
                  variantQuantity += item.quantity;
                }
              });

              if (variantQuantity >= legacyMax) {
                shouldBlock = true;
              }
            }
          }
        }

        if (shouldBlock) {
          this.buttonsPlus.disabled = true;
          this.input.disabled = true;
          return;
        }
      } catch (error) {
        console.error("Error checking max quantity:", error);
      }

      this.isUpdating = true;
      this.buttonsPlus.disabled = true;
      this.buttonsMinus.disabled = true;
      if (this.input) this.input.disabled = true;

      this.input.stepUp();
      this.updateCart(false);
    };

    downQuantity = () => {
      // Prevent spam clicks - if already updating, ignore
      if (this.isUpdating) {
        return;
      }

      this.isUpdating = true;
      this.buttonsPlus.disabled = true;
      this.buttonsMinus.disabled = true;
      if (this.input) this.input.disabled = true;

      // If quantity is 1, remove the item instead of decreasing
      if (parseInt(this.input.value) === 1) {
        this.updateCart(true);
      } else {
        this.input.stepDown();
        this.updateCart(false);
      }
    };

    checkMaxQuantity = async (restrictInput) => {
      // Get the product ID for cart comparison
      const productId = parseInt(this.input.dataset.productId);
      const cartData = await fetch("/cart.js").then((res) => res.json());

      let shouldDisable = false;

      if (this.applyMaxToProduct) {
        // Apply BOTH product-level max AND variant-level max

        // Check product-level max
        const productMax =
          this.productMaxQuantity ||
          (this.dataset.maxQuantity
            ? parseInt(this.dataset.maxQuantity)
            : null);
        if (productMax) {
          let totalProductQuantity = 0;
          cartData.items.forEach((item) => {
            if (item.product_id == productId) {
              totalProductQuantity += item.quantity;
            }
          });

          console.log(
            `QuantitySelector (product-level): Product ${productId}, Current: ${totalProductQuantity}, Max: ${productMax}`
          );

          if (totalProductQuantity >= productMax) {
            shouldDisable = true;
          }
        }

        // Check variant-level max for THIS variant
        if (this.variantMaxQuantity) {
          let variantQuantity = 0;
          cartData.items.forEach((item) => {
            if (item.variant_id == this.variantId) {
              variantQuantity += item.quantity;
            }
          });

          console.log(
            `QuantitySelector (variant-level on product-level): Variant ${this.variantId}, Current: ${variantQuantity}, Max: ${this.variantMaxQuantity}`
          );

          if (variantQuantity >= this.variantMaxQuantity) {
            shouldDisable = true;
          }
        }
      } else {
        // applyMaxToProduct is false: only check variant-level max
        if (this.variantMaxQuantity) {
          let variantQuantity = 0;
          cartData.items.forEach((item) => {
            if (item.variant_id == this.variantId) {
              variantQuantity += item.quantity;
            }
          });

          console.log(
            `QuantitySelector (variant-level): Variant ${this.variantId}, Current: ${variantQuantity}, Max: ${this.variantMaxQuantity}`
          );

          if (variantQuantity >= this.variantMaxQuantity) {
            shouldDisable = true;
          }
        } else {
          // Fall back to legacy maxQuantity
          const legacyMax = this.dataset.maxQuantity
            ? parseInt(this.dataset.maxQuantity)
            : null;
          if (legacyMax) {
            let variantQuantity = 0;
            cartData.items.forEach((item) => {
              if (item.variant_id == this.variantId) {
                variantQuantity += item.quantity;
              }
            });

            console.log(
              `QuantitySelector (legacy): Variant ${this.variantId}, Current: ${variantQuantity}, Max: ${legacyMax}`
            );

            if (variantQuantity >= legacyMax) {
              shouldDisable = true;
            }
          }
        }
      }

      // Also check input max attribute as a final constraint
      const inputMax =
        this.input && this.input.max ? parseInt(this.input.max) : null;
      if (inputMax && parseInt(this.input.value) >= inputMax) {
        shouldDisable = true;
      }

      if (shouldDisable) {
        this.buttonsPlus.disabled = true;
        if (this.input) this.input.disabled = true;
      } else if (this.buttonsPlus) {
        this.buttonsPlus.disabled = false;
        if (this.input) this.input.disabled = false;
      }

      if (restrictInput) {
        // If current input value exceeds the input max attribute, restrict it
        const currentValue = parseInt(this.input.value);
        if (inputMax && currentValue > inputMax) {
          this.input.value = inputMax;
          this.input.dispatchEvent(new Event("change"));
          return false;
        }
      }

      return true;
    };

    //Update Cart Drawer HTML
    updateCart = (remove) => {
      if ("updateItem" in this.dataset) {
        //Get Line Item Data
        const data = {
          line: this.dataset.line * 1,
          quantity: remove ? 0 : this.input.value,
          //Assign Section Id which we want with response after add line item in cart
          sections: ["cart-drawer"],
          sections_url: window.location.pathname,
        };

        const body = JSON.stringify(data);
        fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => {
            return response.text();
          })
          .then((state) => {
            const parsedState = JSON.parse(state);

            // Use shared utility to replace cart drawer content
            replaceCartDrawerContent(parsedState.sections["cart-drawer"]);

            // Update cart count
            document.querySelector(".cart_count_latest").textContent =
              parsedState.item_count;

            // Announce cart update to screen readers
            const liveRegion = document.querySelector(
              'cart-drawer [aria-live="polite"]'
            );
            if (liveRegion) {
              liveRegion.textContent = `Cart updated: ${
                parsedState.item_count
              } item${parsedState.item_count !== 1 ? "s" : ""}`;
            }

            // Publish cart update event for recommendations manager
            if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                cartData: parsedState,
                source: "quantity-selector",
              });
            }

            console.log(state);
          })
          .catch((error) => {
            console.log(error);
          })
          .finally(async () => {
            // Check max quantity on product form after cart update
            // Use multiple timeouts to ensure it catches the update
            const productForms = document.querySelectorAll("product-form");
            const cartResponse = await fetch("/cart.js");
            const cartData = await cartResponse.json();

            productForms.forEach((productForm) => {
              //console.log('Checking max quantity after cart update (immediate)');
              if (
                productForm &&
                typeof productForm.checkMaxQuantity === "function"
              ) {
                //console.log('Checking max quantity after cart update (300ms)');
                productForm.checkMaxQuantity(cartData);
              }
            });

            // Re-enable buttons after update completes
            // Note: this element is being replaced, so we can't re-enable on the old instance
          });
      }
    };
  }

  customElements.define("quantity-selector", QuantitySelector);
}

if (!customElements.get("product-form")) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector("form");
      this.form.addEventListener("submit", this.addProduct.bind(this));
      this.maxQuantity = this.dataset.maxQuantity
        ? parseInt(this.dataset.maxQuantity)
        : null;
      this.productId = this.dataset.productId;

      this.applyMaxToProduct =
        this.dataset.applyMaxToProduct === "true" ||
        this.dataset.applyMaxToProduct === true ||
        !this.dataset.applyMaxToProduct;

      // New metafield-based max quantity attributes
      this.productMaxQuantity = this.dataset.maxQuantity
        ? parseInt(this.dataset.maxQuantity)
        : null;
      this.variantMaxQuantity = this.dataset.variantMaxQuantity
        ? parseInt(this.dataset.variantMaxQuantity)
        : null;

      // Get Algolia queryID if this product came from search
      this.algoliaQueryID = this.dataset.algoliaQueryId || this.algoliaQueryID || null;

      // Check max quantity on page load - always check, return early inside if no max is set
      this.checkMaxQuantity();

      setTimeout(() => {
        let installmentsText = document.querySelector(
          ".pdp-installments-text shopify-payment-terms"
        );
        if (installmentsText) {
          let shadowRoot = installmentsText.shadowRoot;
          if (shadowRoot) {
            shadowRoot.querySelector(
              "#shopify-installments-content div"
            ).style.color = "rgba(90, 49, 244, 1)";
          }
        }
      }, 1000);
    }

    changeAllButtonsDisabledState = (disabled, changeText = true) => {
      const buttons = document.querySelectorAll(
        `product-form[data-product-id="${this.productId}"] button[name="add"]`
      );
      buttons.forEach((button) => {
        if (disabled) {
          button.setAttribute("disabled", "disabled");
        } else {
          button.removeAttribute("disabled");
        }

        if (!changeText) return;
        const buttonText = button.querySelector("span");
        if (buttonText) {
          buttonText.textContent = disabled
            ? "Max Purchase Limit Reached"
            : button.dataset.bundleProduct
            ? "Choose Products"
            : "Add To Cart";
        }
      });
    };

    checkMaxQuantity = async (cartData) => {
      // Return if no max quantity is set anywhere
      if (!this.maxQuantity && !this.productMaxQuantity) return;

      try {
        if (!cartData) {
          const cartResponse = await fetch("/cart.js");
          cartData = await cartResponse.json();
        }

        // Get the current variant ID from the form
        const variantIdInput = this.form.querySelector('input[name="id"]');
        const currentVariantId = variantIdInput
          ? parseInt(variantIdInput.value)
          : null;

        let shouldDisable = false;

        if (this.applyMaxToProduct) {
          // Apply BOTH product-level max AND variant-level max

          // Check product-level max: custom.max_quantity
          const productMax = this.productMaxQuantity || this.maxQuantity;
          if (productMax) {
            let totalProductQuantity = 0;
            cartData.items.forEach((item) => {
              if (item.product_id == this.productId) {
                totalProductQuantity += item.quantity;
              }
            });

            /*console.log(`[ProductForm] Checking product-level max for product ${this.productId}:`, {
                            currentQuantity: totalProductQuantity,
                            effectiveMax: productMax
                        });*/

            if (totalProductQuantity >= productMax) {
              shouldDisable = true;
              //console.log('[ProductForm] Disabling button - product max reached', { totalProductQuantity, productMax });
            }
          }

          // Check variant-level max: custom.max_quantity_per_variant_optional for THIS variant
          if (currentVariantId && this.variantMaxQuantity) {
            const variantMax = parseInt(this.variantMaxQuantity);
            let variantQuantity = 0;
            cartData.items.forEach((item) => {
              if (item.variant_id == currentVariantId) {
                variantQuantity += item.quantity;
              }
            });

            /*
                            console.log(`[ProductForm] Checking variant-level max for variant ${currentVariantId}:`, {
                                currentQuantity: variantQuantity,
                                effectiveMax: variantMax
                            });
                        */

            if (variantQuantity >= variantMax) {
              shouldDisable = true;
              //console.log('[ProductForm] Disabling button - variant max reached', { variantQuantity, variantMax });
            }
          }
        } else {
          // applyMaxToProduct is false: only check variant-level max
          if (currentVariantId && this.variantMaxQuantities[currentVariantId]) {
            const variantMax = parseInt(
              this.variantMaxQuantities[currentVariantId]
            );
            let variantQuantity = 0;
            cartData.items.forEach((item) => {
              if (item.variant_id == currentVariantId) {
                variantQuantity += item.quantity;
              }
            });

            console.log(
              `[ProductForm] Checking variant-level max (applyMaxToProduct=false) for variant ${currentVariantId}:`,
              {
                currentQuantity: variantQuantity,
                effectiveMax: variantMax,
              }
            );

            if (variantQuantity >= variantMax) {
              shouldDisable = true;
              console.log(
                "[ProductForm] Disabling button - variant max reached",
                { variantQuantity, variantMax }
              );
            }
          } else {
            // Fall back to legacy maxQuantity if no per-variant max
            const legacyMax = this.maxQuantity;
            if (legacyMax) {
              let variantQuantity = 0;
              cartData.items.forEach((item) => {
                if (item.variant_id == currentVariantId) {
                  variantQuantity += item.quantity;
                }
              });

              console.log(
                `[ProductForm] Checking legacy max for variant ${currentVariantId}:`,
                {
                  currentQuantity: variantQuantity,
                  effectiveMax: legacyMax,
                }
              );

              if (variantQuantity >= legacyMax) {
                shouldDisable = true;
                console.log(
                  "[ProductForm] Disabling button - legacy max reached",
                  { variantQuantity, legacyMax }
                );
              }
            }
          }
        }

        if (shouldDisable) {
          this.changeAllButtonsDisabledState(true);
        } else {
          this.changeAllButtonsDisabledState(false);
        }
      } catch (error) {
        console.error("Error checking max quantity:", error);
      }
    };

    trackAlgoliaConversion = (cartResponse) => {
      // Check if dataLayer exists (Google Tag Manager)
      if (!window.dataLayer) {
        window.dataLayer = [];
      }

      try {
        // Get product details from the cart response
        const productId = cartResponse.product_id;
        const variantId = cartResponse.variant_id;
        const productHandle = cartResponse.handle;
        const productTitle = cartResponse.product_title;
        const variantTitle = cartResponse.variant_title;
        const price = cartResponse.price / 100; // Convert from cents to dollars
        const quantity = cartResponse.quantity || 1;
        const objectID = productHandle || productId.toString();

        // Push Algolia conversion event to dataLayer (for GTM)
        window.dataLayer.push({
          event: 'algolia_conversion',
          algolia_event_type: 'conversion',
          algolia_object_id: objectID,
          algolia_query_id: this.algoliaQueryID || null,
          algolia_index_name: window.algoliaConfig?.indexName || 'shopify_products',
          ecommerce: {
            currency: Shopify?.currency?.active || 'USD',
            value: price * quantity,
            items: [{
              item_id: variantId.toString(),
              item_name: productTitle,
              item_variant: variantTitle,
              price: price,
              quantity: quantity
            }]
          }
        });

        // Send event to Algolia Insights API
        if (typeof aa === 'function') {
          const indexName = window.algoliaConfig?.indexName || 'shopify_products';

          // If there's a queryID, this is a conversion after search
          if (this.algoliaQueryID) {
            aa('addedToCartObjectIDsAfterSearch', {
              eventName: 'Product Added to Cart',
              index: indexName,
              objectIDs: [objectID],
              queryID: this.algoliaQueryID,
              objectData: [{
                queryID: this.algoliaQueryID,
                price: price,
                quantity: quantity
              }],
              currency: Shopify?.currency?.active || 'USD'
            });

            console.log('Algolia Insights: addedToCartObjectIDsAfterSearch', {
              objectID,
              queryID: this.algoliaQueryID,
              price,
              quantity
            });
          } else {
            // No queryID means this wasn't from search, still track as conversion
            aa('addedToCartObjectIDs', {
              eventName: 'Product Added to Cart (Direct)',
              index: indexName,
              objectIDs: [objectID],
              objectData: [{
                price: price,
                quantity: quantity
              }],
              currency: Shopify?.currency?.active || 'USD'
            });

            console.log('Algolia Insights: addedToCartObjectIDs', {
              objectID,
              price,
              quantity
            });
          }
        } else {
          console.warn('Algolia Insights (aa) not initialized');
        }

        console.log('Algolia conversion tracked:', {
          productHandle,
          productTitle,
          variantId,
          queryID: this.algoliaQueryID
        });
      } catch (error) {
        console.error('Error tracking Algolia conversion:', error);
      }
    };

    addProduct = async (event) => {
      event.preventDefault();

      // Pre-emptively check if adding would exceed max
      if (this.maxQuantity || this.productMaxQuantity) {
        try {
          const cartResponse = await fetch("/cart.js");
          const cartData = await cartResponse.json();

          // Get the current variant ID from the form
          const variantIdInput = this.form.querySelector('input[name="id"]');
          const currentVariantId = variantIdInput
            ? parseInt(variantIdInput.value)
            : null;

          let maxReached = false;

          if (this.applyMaxToProduct) {
            // Apply BOTH product-level max AND variant-level max

            // Check product-level max
            const productMax = this.productMaxQuantity || this.maxQuantity;
            if (productMax) {
              let totalProductQuantity = 0;
              cartData.items.forEach((item) => {
                if (item.product_id == this.productId) {
                  totalProductQuantity += item.quantity;
                }
              });

              if (totalProductQuantity >= productMax) {
                maxReached = true;
                console.log("[ProductForm] Add blocked - product max reached", {
                  totalProductQuantity,
                  productMax,
                });
              }
            }

            // Check variant-level max for THIS variant
            if (
              currentVariantId &&
              this.variantMaxQuantities[currentVariantId]
            ) {
              const variantMax = parseInt(
                this.variantMaxQuantities[currentVariantId]
              );
              let variantQuantity = 0;
              cartData.items.forEach((item) => {
                if (item.variant_id == currentVariantId) {
                  variantQuantity += item.quantity;
                }
              });

              if (variantQuantity >= variantMax) {
                maxReached = true;
                console.log("[ProductForm] Add blocked - variant max reached", {
                  variantQuantity,
                  variantMax,
                });
              }
            }
          } else {
            // applyMaxToProduct is false: only check variant-level max
            if (
              currentVariantId &&
              this.variantMaxQuantities[currentVariantId]
            ) {
              const variantMax = parseInt(
                this.variantMaxQuantities[currentVariantId]
              );
              let variantQuantity = 0;
              cartData.items.forEach((item) => {
                if (item.variant_id == currentVariantId) {
                  variantQuantity += item.quantity;
                }
              });

              if (variantQuantity >= variantMax) {
                maxReached = true;
                console.log(
                  "[ProductForm] Add blocked - variant max reached (applyMaxToProduct=false)",
                  { variantQuantity, variantMax }
                );
              }
            } else {
              // Fall back to legacy maxQuantity
              const legacyMax = this.maxQuantity;
              if (legacyMax) {
                let variantQuantity = 0;
                cartData.items.forEach((item) => {
                  if (item.variant_id == currentVariantId) {
                    variantQuantity += item.quantity;
                  }
                });

                if (variantQuantity >= legacyMax) {
                  maxReached = true;
                  console.log(
                    "[ProductForm] Add blocked - legacy max reached",
                    { variantQuantity, legacyMax }
                  );
                }
              }
            }
          }

          if (maxReached) {
            this.changeAllButtonsDisabledState(true);
            return;
          }
        } catch (error) {
          console.error("Error checking max quantity:", error);
        }
      }

      const config = fetchConfig("javascript");
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      delete config.headers["Content-Type"];

      const formData = new FormData(this.form);
      //Assign Section Id which we want with response after add line item in cart
      if (document.querySelector("cart-drawer")) {
        formData.append("sections", ["cart-drawer"]);
        formData.append("sections_url", window.location.pathname);
      }

      config.body = formData;

      // Disable button immediately to prevent double-clicks
      if (this.maxQuantity || this.productMaxQuantity) {
        // If there's a max quantity, disable all buttons for this product to prevent overshoot
        this.changeAllButtonsDisabledState(true, false);
      }

      // Add Product into Cart
      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          // Track Algolia conversion event
          this.trackAlgoliaConversion(response);

          // Use shared utility to replace cart drawer content
          replaceCartDrawerContent(response.sections["cart-drawer"]);

          fetch("/cart.js")
            .then((response) => response.json())
            .then((data) => {
              let cartCountElement =
                document.querySelector(".cart_count_latest");
              if (cartCountElement) {
                cartCountElement.textContent = data.item_count;
              }
              document
                .querySelectorAll(".cart_count_latest_box")
                .forEach((button) => {
                  button.style.display = "flex";
                });

              // Publish cart update event for recommendations manager
              if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
                publish(PUB_SUB_EVENTS.cartUpdate, {
                  cartData: data,
                  source: "product-form",
                });
              }

              // Check max quantity after adding to cart
              this.checkMaxQuantity(data);
            })
            .catch((error) => console.error("Cart Count Update Error:", error));
        })
        .catch((error) => {
          console.log(error);
          // Re-enable button on error
          this.changeAllButtonsDisabledState(false);
        })
        .finally(() => {
          setTimeout(() => {
            document.querySelector("cart-drawer").openDrawer();
          }, 500);
        });
    };
  }

  customElements.define("product-form", ProductForm);
}

document
  .querySelector(".cart-link")
  .addEventListener("click", function (event) {
    event.preventDefault();
    document.querySelector("cart-drawer").openDrawer();
  });

var cartBox = document.getElementById("CartDrawer-Form");
if (cartBox) {
  var itemCount = parseInt(cartBox.getAttribute("data-item-count"));
  if (itemCount >= 3) {
    cartBox.classList.add("form-reset_addd3");
  }
}
